import { isJsonRecord } from './emailTemplateJsonSpec';

/**
 * Progressive parsing for streaming email-template proposals.
 *
 * While the agent writes the `propose_email_template_spec` tool call, we
 * receive the accumulating JSON text (the `jsonSpec` field is nested inside
 * the tool input). That text is almost always syntactically incomplete —
 * a half-written string, an open brace, a trailing comma. This module makes a
 * best-effort attempt to turn that partial text into a *renderable* email spec
 * so the preview can grow as the model writes.
 *
 * Strategy:
 *  1. Close any open strings / brackets / braces so `JSON.parse` succeeds.
 *  2. Extract the `jsonSpec` object (the tool input wraps it).
 *  3. Prune the flat element map so it only references elements that are fully
 *     present, dropping dangling child references. This keeps the spec valid
 *     for the renderer even mid-stream.
 *
 * Returns `null` when nothing renderable can be salvaged yet.
 */

/**
 * Balance a partial JSON string by closing open strings, arrays, and objects.
 *
 * Streamed JSON can be truncated mid-token in many ways (an open string, a
 * written key with no value, a dangling comma or colon, a half-typed literal).
 * Rather than enumerate every case, we find the longest *safe prefix* — the
 * prefix ending at a token boundary where the value most recently added to
 * each open container is complete — then append the closing brackets for the
 * still-open containers. The result is parsed to confirm validity, trimming
 * back further if the first attempt fails.
 */
export function completePartialJson(input: string): string | null {
  const text = input.trimEnd();
  if (!text) return null;

  // Boundaries (1-based end indices) after which the text-so-far ends on a
  // completed value within its container: a closed string, a closed bracket,
  // or a finished literal followed by a structural char. We try the longest
  // first and fall back to shorter ones until one balances and parses.
  const boundaries = collectSafeBoundaries(text);

  for (let i = boundaries.length - 1; i >= 0; i--) {
    const candidate = balanceAndClose(text.slice(0, boundaries[i]));
    if (candidate === null) continue;
    try {
      JSON.parse(candidate);
      return candidate;
    } catch {
      // try a shorter prefix
    }
  }
  return null;
}

/**
 * End indices (exclusive) of prefixes that plausibly end on a complete value.
 * A boundary sits immediately after a closing quote, `}`, `]`, or a literal
 * token (number/true/false/null) — i.e. wherever a value could legally end.
 */
function collectSafeBoundaries(text: string): number[] {
  const boundaries: number[] = [];
  let inString = false;
  let escaped = false;
  let literalRun = false;

  function flushLiteral(end: number) {
    if (literalRun) {
      boundaries.push(end);
      literalRun = false;
    }
  }

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') {
        inString = false;
        boundaries.push(i + 1);
      }
      continue;
    }
    switch (ch) {
      case '"':
        flushLiteral(i);
        inString = true;
        break;
      case '}':
      case ']':
        flushLiteral(i);
        boundaries.push(i + 1);
        break;
      case '{':
      case '[':
        // A position right after an opening bracket is a safe fallback: it
        // yields an empty container when the first key/value is incomplete.
        flushLiteral(i);
        boundaries.push(i + 1);
        break;
      case ':':
      case ',':
        flushLiteral(i);
        break;
      case ' ':
      case '\n':
      case '\r':
      case '\t':
        flushLiteral(i);
        break;
      default:
        // part of a literal token (number / true / false / null)
        literalRun = true;
        break;
    }
  }
  flushLiteral(text.length);
  return boundaries;
}

/**
 * Append the closing brackets needed to balance `text`. Returns null if a
 * string is still open (the prefix was not cut at a safe boundary) or if the
 * prefix ends on a dangling `:` / `,` that can't be closed.
 */
function balanceAndClose(prefix: string): string | null {
  let trimmed = prefix.trimEnd();
  // A boundary can legitimately leave a trailing comma (e.g. after a closed
  // value) — strip dangling separators before closing.
  while (trimmed.endsWith(',') || trimmed.endsWith(':')) {
    trimmed = trimmed.slice(0, -1).trimEnd();
  }
  if (!trimmed) return null;

  const stack: Array<'{' | '['> = [];
  let inString = false;
  let escaped = false;
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' || ch === ']') stack.pop();
  }
  if (inString) return null;

  const closers = stack
    .reverse()
    .map((open) => (open === '{' ? '}' : ']'))
    .join('');
  return trimmed + closers;
}

/**
 * Prune a flat email-spec element map so every retained element is complete
 * and only references children that exist. Returns null when no root element
 * is renderable yet.
 */
function pruneEmailSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> | null {
  const root = spec.root;
  if (typeof root !== 'string' || !root.trim()) return null;
  if (!isJsonRecord(spec.elements)) return null;

  const sourceElements = spec.elements;

  function isCompleteElement(value: unknown): value is Record<string, unknown> {
    return (
      isJsonRecord(value) &&
      typeof value.type === 'string' &&
      value.type.trim().length > 0
    );
  }

  if (!isCompleteElement(sourceElements[root])) return null;

  const pruned: Record<string, unknown> = {};

  for (const [key, element] of Object.entries(sourceElements)) {
    if (!isCompleteElement(element)) continue;
    const next: Record<string, unknown> = { ...element };
    if (Array.isArray(element.children)) {
      next.children = element.children.filter(
        (child) =>
          typeof child === 'string' && isCompleteElement(sourceElements[child]),
      );
    } else if ('children' in element) {
      // Streaming can truncate `children` into a scalar/object — drop the
      // malformed value rather than leaking it into the rendered spec.
      delete next.children;
    }
    pruned[key] = next;
  }

  if (!pruned[root]) return null;

  return { ...spec, root, elements: pruned };
}

/**
 * Parse the accumulated partial tool-input text for
 * `propose_email_template_spec` into a renderable email spec, pruned to
 * what is currently complete. Returns the pruned spec object, or null when
 * nothing renderable can be salvaged yet.
 *
 * `partialInput` is the JSON text of the *whole tool input*, whose shape is
 * `{ jsonSpec: {...}, sampleVariables: {...}, ... }`.
 */
export function parsePartialProposalSpec(
  partialInput: string,
): Record<string, unknown> | null {
  const completed = completePartialJson(partialInput);
  if (!completed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(completed);
  } catch {
    return null;
  }

  if (!isJsonRecord(parsed)) return null;
  const jsonSpec = parsed.jsonSpec;
  if (!isJsonRecord(jsonSpec)) return null;

  return pruneEmailSpec(jsonSpec);
}
